import React, { useState, useEffect, useRef } from 'react';
import { authenticatedFetch, getUserProfile } from '../utils/auth';
import { getUserMapPreference, getMapOptions } from '../utils/mapUtils';
import PdfButton from './common/PdfButton';
import { generateUnifiedPDF, generateSearchEvidencePDF } from '../utils/pdfGenerator';
import InteractiveMap from './common/InteractiveMap';
import { handleMapClick } from '../utils/mapClickHandler';
import { useLocationDetection } from '../hooks/useLocationDetection';

const mapContainerStyle = {
  width: '100%',
  height: '100%',
  flex: '1 1 0%'
};

function ResultDisplay({ from, to, roadDistance, roadDuration, aerialDistance, loading, fromLabel = 'Address 1', toLabel = 'Address 2', isLocationInIndiaParam = true, onGeneratePDF = null }) {
  const [copied, setCopied] = React.useState({});
  const [hovered, setHovered] = React.useState({});
  const [pdfLoading, setPdfLoading] = React.useState(false);
  
  const handleCopy = (key, value, e) => {
    if (!value || value === 'N/A') return;
    navigator.clipboard.writeText(value);
    setCopied(prev => ({ ...prev, [key]: true }));
    const button = e?.target;
    if (button) {
      const originalText = button.textContent;
      const originalBg = button.style.backgroundColor;
      button.textContent = 'Copied!';
      button.style.backgroundColor = '#28a745';
      setTimeout(() => {
        button.textContent = originalText;
        button.style.backgroundColor = originalBg;
        setCopied(prev => ({ ...prev, [key]: false }));
      }, 1500);
    } else {
      setTimeout(() => setCopied(prev => ({ ...prev, [key]: false })), 1500);
    }
  };

  const handleGeneratePDF = async () => {
    if (!onGeneratePDF) return;
    setPdfLoading(true);
    try {
      await onGeneratePDF();
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setPdfLoading(false);
    }
  };

  const renderCopy = (key, value) => (
    <button
      title="Copy"
      onClick={e => handleCopy(key, value, e)}
      disabled={!value || value === 'N/A'}
      style={{
        backgroundColor: copied[key] ? '#28a745' : '#6c757d',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        fontSize: '12px',
        cursor: (!value || value === 'N/A') ? 'not-allowed' : 'pointer',
        padding: '4px 10px',
        marginLeft: 8,
        fontWeight: 500,
        transition: 'background-color 0.2s, opacity 0.2s',
        outline: 'none',
        position: 'relative',
        minWidth: 48,
        opacity: hovered[key] ? 1 : 0,
        pointerEvents: hovered[key] ? 'auto' : 'none',
        display: 'inline-block',
      }}
    >
      {copied[key] ? 'Copied!' : 'Copy'}
    </button>
  );

  const hoverHandlers = key => ({
    onMouseEnter: () => setHovered(prev => ({ ...prev, [key]: true })),
    onMouseLeave: () => setHovered(prev => ({ ...prev, [key]: false })),
  });

  return (
    <div style={{ marginTop: 16 }}>
      {loading ? (
        <div style={{ textAlign: 'center', color: '#888', fontSize: 15 }}>Loading address and digipin...</div>
      ) : (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
          <div style={{ flex: 1, minWidth: 180, background: '#fff', border: '1px solid #eee', borderRadius: 8, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
              <h4 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#333' }}>{fromLabel}</h4>
              <div style={{ display: 'flex', gap: 4 }}>
                {renderCopy('from-address', from.address)}
                {renderCopy('from-digipin', from.digipin)}
              </div>
            </div>
            <div {...hoverHandlers('from-address')} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>Address:</div>
              <div style={{ fontSize: 15, color: '#333', fontWeight: 500, wordBreak: 'break-word' }}>
                {from.address}
              </div>
            </div>
            <div {...hoverHandlers('from-digipin')} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>Digipin:</div>
              <div style={{ fontSize: 15, color: '#333', fontWeight: 500, fontfamily: 'monospace' }}>
                {from.digipin}
              </div>
            </div>
            <div style={{ fontSize: 14, color: '#666' }}>
              Coordinates: {from.lat}, {from.lng}
            </div>
          </div>

          <div style={{ flex: 1, minWidth: 180, background: '#fff', border: '1px solid #eee', borderRadius: 8, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
              <h4 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#333' }}>{toLabel}</h4>
              <div style={{ display: 'flex', gap: 4 }}>
                {renderCopy('to-address', to.address)}
                {renderCopy('to-digipin', to.digipin)}
              </div>
            </div>
            <div {...hoverHandlers('to-address')} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>Address:</div>
              <div style={{ fontSize: 15, color: '#333', fontWeight: 500, wordBreak: 'break-word' }}>
                {to.address}
              </div>
            </div>
            <div {...hoverHandlers('to-digipin')} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>Digipin:</div>
              <div style={{ fontSize: 15, color: '#333', fontWeight: 500, fontfamily: 'monospace' }}>
                {to.digipin}
              </div>
            </div>
            <div style={{ fontSize: 14, color: '#666' }}>
              Coordinates: {to.lat}, {to.lng}
            </div>
          </div>
        </div>
      )}

      {!loading && (
        <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 8, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: 16, fontWeight: 600, color: '#333' }}>Distance Results</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>Aerial Distance</div>
              <div style={{ fontSize: 18, fontWeight: 600, color: '#007bff' }}>
                {aerialDistance ? `${aerialDistance.toFixed(2)} km` : 'N/A'}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>Road Distance</div>
              <div style={{ fontSize: 18, fontWeight: 600, color: '#28a745' }}>
                {roadDistance ? `${roadDistance.toFixed(2)} km` : 'N/A'}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>Road Duration</div>
              <div style={{ fontSize: 18, fontWeight: 600, color: '#ffc107' }}>
                {roadDuration ? roadDuration : 'N/A'}
              </div>
            </div>
          </div>
          {onGeneratePDF && (
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <PdfButton onGeneratePDF={handleGeneratePDF} loading={pdfLoading} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const RouteIdentifierPage = () => {
  const [address, setAddress] = useState('');
  const [distances, setDistances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [newAddressCoords, setNewAddressCoords] = useState(null);
  const [placeId, setPlaceId] = useState(null);
  const [mapUrl, setMapUrl] = useState('');
  const [featureTab, setFeatureTab] = useState('route');
  const [mapMarkers, setMapMarkers] = useState([]);
  const [mapClickLoading, setMapClickLoading] = useState(false);
  const [mapType, setMapType] = useState('roadmap');
  const [satelliteLabels, setSatelliteLabels] = useState(true);
  const [directions, setDirections] = useState(null);
  const [mapRefreshKey, setMapRefreshKey] = useState(0);

  const {
    userLocation,
    isLocationInIndiaState,
    locationLoading,
    locationSource,
    showLocationMessage,
    defaultMapCenter,
    setDefaultMapCenter,
    setShowLocationMessage
  } = useLocationDetection();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await authenticatedFetch('/distance', {
        method: 'POST',
        body: JSON.stringify({
          address: address,
          place_id: placeId
        })
      });

      if (response.ok) {
        const data = await response.json();
        setDistances(data.distances);
        setNewAddressCoords(data.new_address_coords);
        setMapMarkers(data.distances.map(dist => ({
          lat: parseFloat(dist.latitude),
          lng: parseFloat(dist.longitude),
          label: dist.label || dist.predefined_address
        })));
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to calculate distances');
      }
    } catch (error) {
      setError('Error calculating distances');
    } finally {
      setLoading(false);
    }
  };

  const clearFindNearest = () => {
    setAddress('');
    setDistances([]);
    setError('');
    setNewAddressCoords(null);
    setMapMarkers([]);
    setPlaceId(null);
  };

  const clearRoute = () => {
    setDirections(null);
  };

  const handleMapClick = async (lat, lng) => {
    setMapClickLoading(true);
    try {
      const result = await handleMapClick(lat, lng);
      if (result) {
        setAddress(result.address);
        setPlaceId(result.place_id);
      }
    } catch (error) {
      console.error('Error handling map click:', error);
    } finally {
      setMapClickLoading(false);
    }
  };

  const generateFindNearestPDF = async (dist, fromData, toData) => {
    try {
      await generateUnifiedPDF({
        title: 'Route Optimizer Results',
        from: fromData,
        to: toData,
        roadDistance: dist.road_distance_km,
        roadDuration: dist.road_duration,
        aerialDistance: dist.aerial_distance_km,
        isLocationInIndia: isLocationInIndiaState
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ flex: 1, overflow: 'auto' }}>
        <div style={{ display: 'flex', height: '100%', width: '100%' }}>
          {/* Left Panel */}
          <div style={{ width: '30%', padding: '24px', boxSizing: 'border-box', overflowY: 'auto', borderRight: '1px solid #eee', display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
              <button 
                onClick={() => { 
                  setFeatureTab('route'); 
                  setMapMarkers([]); 
                  setAddress(''); 
                  setDistances([]); 
                  setError(''); 
                  setMapUrl(''); 
                  setMapClickLoading(false);
                  clearRoute();
                }} 
                style={{ 
                  flex: 1, 
                  padding: '10px 0', 
                  fontWeight: featureTab === 'route' ? 'bold' : 'normal', 
                  color: featureTab === 'route' ? '#007bff' : '#333', 
                  border: 'none', 
                  borderBottom: featureTab === 'route' ? '2px solid #007bff' : '2px solid #eee', 
                  background: 'none', 
                  cursor: 'pointer' 
                }}
              >
                Find Nearest
              </button>
            </div>

            {featureTab === 'route' && (
              <>
                <h2 style={{ marginBottom: 24 }}>Find Nearest</h2>
                <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#e3f2fd', borderRadius: 6, fontSize: 14, color: '#1976d2' }}>
                  <strong>Tip:</strong> Click on the map to select a location, or type an address manually.
                </div>
                <form onSubmit={handleSubmit} style={{ marginBottom: 24 }}>
                  <input
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="Enter a new address"
                    required
                    style={{ width: '100%', padding: 12, border: '1px solid #ccc', borderRadius: 6, fontSize: 16, marginBottom: 8, boxSizing: 'border-box' }}
                  />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="submit" disabled={loading} style={{ flex: 1, padding: '12px 24px', background: '#007bff', color: 'white', border: 'none', borderRadius: 4, fontSize: 16, cursor: 'pointer' }}>
                      {loading ? 'Calculating...' : 'Calculate Distance'}
                    </button>
                    <button type="button" onClick={clearFindNearest} style={{ padding: '12px 16px', background: '#6c757d', color: 'white', border: 'none', borderRadius: 4, fontSize: 16, cursor: 'pointer' }} title="Clear all">
                      Clear
                    </button>
                  </div>
                </form>
                {error && <div style={{ color: 'red', background: '#f8d7da', padding: 12, borderRadius: 4, textAlign: 'center' }}>{error}</div>}
                {distances.length > 0 && (
                  <div>
                    <h3 style={{ marginBottom: 16 }}>Results</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      {distances.map((dist, index) => (
                        <ResultDisplay
                          key={index}
                          from={{
                            address: newAddressCoords?.address || address,
                            digipin: 'N/A',
                            lat: newAddressCoords?.lat,
                            lng: newAddressCoords?.lng,
                          }}
                          to={{
                            address: dist.label || dist.predefined_address,
                            digipin: 'N/A',
                            lat: dist.latitude,
                            lng: dist.longitude,
                          }}
                          roadDistance={dist.road_distance_km}
                          roadDuration={dist.road_duration}
                          aerialDistance={dist.aerial_distance_km}
                          loading={false}
                          fromLabel="Address 1"
                          toLabel="Address 2"
                          isLocationInIndiaParam={isLocationInIndiaState}
                          onGeneratePDF={() => generateFindNearestPDF(dist, {
                            address: address,
                            digipin: 'N/A',
                            lat: newAddressCoords?.lat,
                            lng: newAddressCoords?.lng,
                          }, {
                            address: dist.label || dist.predefined_address,
                            digipin: 'N/A',
                            lat: dist.latitude,
                            lng: dist.longitude,
                          })}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Right Panel */}
          <div style={{ width: '70%', display: 'flex', flexDirection: 'column', backgroundColor: '#f8f9fa' }}>
            {!locationLoading ? (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <div style={{ flex: 1, position: 'relative' }}>
                  <InteractiveMap
                    key={`map-${defaultMapCenter.lat}-${defaultMapCenter.lng}-${mapRefreshKey}`}
                    markerPositions={mapMarkers.length > 0 ? mapMarkers : (userLocation ? [userLocation] : [])}
                    clickable={true}
                    onMapClick={handleMapClick}
                    defaultCenter={defaultMapCenter}
                    mapType={mapType}
                    satelliteLabels={satelliteLabels}
                    directions={directions}
                  />
                  {mapClickLoading && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 1000
                    }}>
                      <div style={{
                        background: 'white',
                        padding: '20px',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        textAlign: 'center'
                      }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          border: '4px solid #f3f3f3',
                          borderTop: '4px solid #007bff',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                          margin: '0 auto 10px'
                        }}></div>
                        <p style={{ margin: 0, color: '#666' }}>Getting location details...</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
                <div style={{ textAlign: 'center' }}>
                  <p>{locationLoading ? 'Detecting your location...' : 'Loading map...'}</p>
                  <p style={{ fontSize: '14px', marginTop: '10px' }}>
                    {locationLoading ? 'Please wait while we detect your location.' : 'Please wait while we load the map.'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RouteIdentifierPage; 